import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Play } from "lucide-react";
import { DynIcon } from "@/lib/icons";
import { logEvent } from "@/lib/kioskData";
import { IndiaJourneyMap } from "../IndiaJourneyMap";
import { AutoFit } from "../AutoFit";
import { KioskButton, SectionLabel, TopBar, ScreenShell, Pill, useKiosk } from "../ui";

export function IndustriesScreen() {
  const { industries, nav } = useKiosk();
  const list = (industries || []).filter((i) => i.status === "published");

  return (
    <ScreenShell>
      <TopBar title="Industry Applications" onBack={() => nav("solutions")} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-5" data-testid="kiosk-industry-applications-grid">
        <p className="mb-6 text-sm sm:text-base text-slate-400">
          Technology for a stronger, safer and more connected India.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {list.map((ind, i) => (
            <motion.button
              key={ind.id}
              data-testid={`kiosk-industry-card-${i}`}
              onClick={() => { logEvent("industry_opened", ind.name, ind.id); nav("industry", { industry: ind }); }}
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="group flex flex-col items-center gap-3 rounded-2xl glass glow-border-hover p-5 text-center min-h-[140px] justify-center"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-400/40 text-cyan-300 group-active:cyan-grad group-active:text-[#041021] transition-colors">
                <DynIcon name={ind.icon} className="h-6 w-6" />
              </div>
              <span className="font-head text-sm font-semibold text-white leading-tight">{ind.name}</span>
            </motion.button>
          ))}
        </div>
      </div>
    </ScreenShell>
  );
}

export function IndustryDetail({ industry }) {
  const { back, solutions, nav } = useKiosk();
  const ind = industry;
  const related = (solutions || []).filter((s) => (ind.solution_ids || []).includes(s.id) || (s.industries || []).includes(ind.name));

  return (
    <ScreenShell>
      <TopBar title={ind.name} onBack={back} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-5" data-testid="kiosk-industry-detail-view">
        <AutoFit>
        <div className="grid grid-cols-1 content-start gap-x-8 gap-y-4 landscape:grid-cols-2 landscape:items-start">
          {ind.cover_image_url && (
            <div className="relative overflow-hidden rounded-3xl glow-border landscape:col-start-1 landscape:row-start-1">
              <img src={ind.cover_image_url} alt={ind.name} className="h-44 landscape:h-72 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050b18]/80 to-transparent" />
            </div>
          )}
          <div className="landscape:col-start-2 landscape:row-start-1">
            <p className="text-base leading-relaxed text-slate-300">{ind.description}</p>

        {(ind.challenges || []).length > 0 && (
          <div className="mt-4">
            <SectionLabel>Challenges</SectionLabel>
            <div className="mt-4 space-y-2.5">
              {ind.challenges.map((c) => (
                <div key={c} className="flex items-start gap-3 rounded-2xl glass px-4 py-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
                  <span className="text-sm text-slate-200">{c}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(ind.benefits || []).length > 0 && (
          <div className="mt-4">
            <SectionLabel>Benefits</SectionLabel>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {ind.benefits.map((b) => <Pill key={b}>{b}</Pill>)}
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-4">
            <SectionLabel>Relevant Solutions</SectionLabel>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((s) => (
                <button
                  key={s.id}
                  data-testid={`kiosk-industry-solution-${s.id}`}
                  onClick={() => { logEvent("solution_opened", s.name, s.id); nav("solution", { solution: s }); }}
                  className="flex items-center gap-3 rounded-2xl glass glow-border-hover px-4 py-3 text-left"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl cyan-grad text-[#041021]">
                    <DynIcon name={s.icon} className="h-5 w-5" />
                  </div>
                  <span className="font-head text-sm font-semibold text-white">{s.name}</span>
                  <ChevronRight className="ml-auto h-4 w-4 text-cyan-400/70" />
                </button>
              ))}
            </div>
          </div>
        )}

        {(ind.case_studies || []).length > 0 && (
          <div className="mt-4">
            <SectionLabel>Case Studies</SectionLabel>
            <div className="mt-4 space-y-3">
              {ind.case_studies.map((c, i) => (
                <div key={i} className="rounded-2xl glass p-4">
                  <h4 className="font-head font-semibold text-white">{c.title}</h4>
                  <p className="mt-1 text-sm text-slate-300">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        </div>
        </AutoFit>
      </div>
    </ScreenShell>
  );
}

export function JourneyScreen() {
  const { journey, nav, setSuppressIdle } = useKiosk();
  const j = journey || {};
  const stages = j.stages || [];
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return;
    setSuppressIdle(true);
    const id = setInterval(() => {
      setActive((a) => {
        if (a >= stages.length - 1) { clearInterval(id); setPlaying(false); setSuppressIdle(false); return a; }
        return a + 1;
      });
    }, 1400);
    return () => { clearInterval(id); setSuppressIdle(false); };
  }, [playing, stages.length, setSuppressIdle]);

  const play = () => { setActive(0); setPlaying(true); };

  return (
    <ScreenShell>
      <TopBar title={j.title || "Journey Visibility"} onBack={() => nav("solutions")} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-5" data-testid="kiosk-journey-visibility-map">
        <p className="text-base sm:text-lg text-cyan-100/90">{j.subtitle || "Track. Understand. Stay Ahead."}</p>

        <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-6 landscape:grid-cols-2 landscape:items-start">
          <div className="space-y-3 landscape:col-start-1 landscape:row-start-1">
            {stages.map((st, i) => {
              const reached = i <= active;
              const isActive = i === active;
              return (
                <button
                  key={st.title}
                  data-testid={`kiosk-journey-stage-${i}`}
                  onClick={() => { setPlaying(false); setActive(i); }}
                  className={`flex w-full items-start gap-4 rounded-2xl glass p-4 text-left transition-all duration-300 ${isActive ? "glow-border" : "glow-border-hover opacity-80"}`}
                >
                  <div className="relative flex flex-col items-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300"
                      style={{ background: reached ? "linear-gradient(135deg,#00e5ff,#0088ff)" : "rgba(18,34,68,0.9)", color: reached ? "#041021" : "#64748b", boxShadow: reached ? "0 0 16px rgba(0,229,255,0.45)" : "none" }}>
                      <DynIcon name={st.icon} className="h-6 w-6" />
                    </div>
                    {i < stages.length - 1 && <span className="mt-1 h-6 w-px" style={{ background: i < active ? "#00e5ff" : "rgba(0,229,255,0.25)" }} />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-tele text-[10px] text-cyan-400/80">0{i + 1}</span>
                      <h3 className="font-head text-lg font-bold text-white">{st.title}</h3>
                    </div>
                    <p className="mt-1 text-sm text-slate-300">{st.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="space-y-4 landscape:col-start-2 landscape:row-start-1 landscape:sticky landscape:top-0">
            <IndiaJourneyMap stages={stages} active={active} onSelect={(i) => { setPlaying(false); setActive(i); }} imageUrl={j.map_image_url} />
            <div className="flex items-center justify-between gap-3">
              <KioskButton testId="kiosk-journey-play" onClick={play} className="flex-1">
                <Play className="h-5 w-5" /> {playing ? "Playing…" : "Watch Animation"}
              </KioskButton>
            </div>
            {j.note && (
              <p className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 px-4 py-3 text-xs text-slate-400">{j.note}</p>
            )}
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}
